import { createGridGeometry, resolveProjection, scaleTileExtent, scaleTileRadius } from './projection';

// Flat-top hex constants
const SQRT3 = Math.sqrt(3);
const HEX_PROJECTION = {
  scale: 1.5,
  stepXFactor: 0.75,
  stepYFactor: SQRT3 * 0.5,
  renderWidthFactor: 1,
  renderHeightFactor: SQRT3 * 0.5,
} as const;
function columnYOffset(q: number, hexHeight: number): number {
  const baseCol = Math.floor(q);
  const t = q - baseCol;
  const evenOffset = (baseCol & 1) ? hexHeight * 0.5 : 0;
  const oddOffset = ((baseCol + 1) & 1) ? hexHeight * 0.5 : 0;
  return evenOffset + (oddOffset - evenOffset) * t;
}

// Odd-q offset neighbors: [N, NE, SE, S, SW, NW]
// For flat-top odd-q offset coordinates, neighbors differ based on column parity
const EVEN_COL_NEIGHBORS = [
  { x:  0, y: -1 },  // N
  { x:  1, y: -1 },  // NE
  { x:  1, y:  0 },  // SE
  { x:  0, y:  1 },  // S
  { x: -1, y:  0 },  // SW
  { x: -1, y: -1 },  // NW
];

const ODD_COL_NEIGHBORS = [
  { x:  0, y: -1 },  // N
  { x:  1, y:  0 },  // NE
  { x:  1, y:  1 },  // SE
  { x:  0, y:  1 },  // S
  { x: -1, y:  1 },  // SW
  { x: -1, y:  0 },  // NW
];

// Convert odd-q offset to cube coordinates for distance calculations
function offsetToCube(q: number, r: number): { x: number; y: number; z: number } {
  const cx = q;
  const cz = r - (q - (q & 1)) / 2;
  const cy = -cx - cz;
  return { x: cx, y: cy, z: cz };
}

// Convert cube coordinates back to odd-q offset
function cubeToOffset(cx: number, _cy: number, cz: number): { q: number; r: number } {
  const q = cx;
  const r = cz + (cx - (cx & 1)) / 2;
  return { q, r };
}

// Cube round for pixel-to-tile conversion
function cubeRound(fx: number, fy: number, fz: number): { x: number; y: number; z: number } {
  let rx = Math.round(fx);
  let ry = Math.round(fy);
  let rz = Math.round(fz);

  const xDiff = Math.abs(rx - fx);
  const yDiff = Math.abs(ry - fy);
  const zDiff = Math.abs(rz - fz);

  if (xDiff > yDiff && xDiff > zDiff) {
    rx = -ry - rz;
  } else if (yDiff > zDiff) {
    ry = -rx - rz;
  } else {
    rz = -rx - ry;
  }

  return { x: rx, y: ry, z: rz };
}

export const hexGrid = createGridGeometry({
  projection: HEX_PROJECTION,
  directions: 6,
  facingAngles: [0, 60, 120, 180, 240, 300],
  rotationStep: 60,
  movementAngleTolerance: 30,
  // QWEASD hex directions:
  // W=N(0deg), E=NE(60deg), D=SE(120deg), S=South(180deg), A=SW(240deg), Q=NW(300deg)
  keyDirections: {
    w: { dx: 0, dy: -1, facing: 0 },
    e: { dx: 1, dy: 0, facing: 60 },    // placeholder; actual offset depends on column parity
    d: { dx: 1, dy: 0, facing: 120 },   // placeholder
    s: { dx: 0, dy: 1, facing: 180 },
    a: { dx: -1, dy: 0, facing: 240 },  // placeholder
    q: { dx: -1, dy: 0, facing: 300 },  // placeholder
  },
  getNextEdgeFacing(currentFacing: number, direction: 'left' | 'right') {
    const normalized = ((currentFacing % 360) + 360) % 360;
    const snapped = Math.round(normalized / 60) * 60;
    const next = snapped + (direction === 'left' ? -60 : 60);
    return ((next % 360) + 360) % 360;
  },
  tileToPixel(q: number, r: number, size: number) {
    const projection = resolveProjection(size, HEX_PROJECTION);
    const x = q * projection.stepX + projection.offsetX;
    const y = r * projection.stepY + columnYOffset(q, projection.stepY) + projection.offsetY;
    return { x, y };
  },
  pixelToTile(px: number, py: number, size: number) {
    const projection = resolveProjection(size, HEX_PROJECTION);
    // Convert from top-left tile-space to center-space expected by hex axial math.
    const radius = scaleTileRadius(size, HEX_PROJECTION);
    const centerX = px - size * 0.5 - projection.offsetX;
    const centerY = py - size * 0.5 - projection.offsetY;

    // Reverse of tileToPixel: convert pixel to fractional cube, round, convert to offset
    // From pixel to fractional axial (flat-top):
    const fq = (2 / 3 * centerX) / radius;
    const fr = (-1 / 3 * centerX + SQRT3 / 3 * centerY) / radius;

    // Axial to cube
    const fx = fq;
    const fz = fr;
    const fy = -fx - fz;

    const cube = cubeRound(fx, fy, fz);
    const offset = cubeToOffset(cube.x, cube.y, cube.z);
    return { q: offset.q, r: offset.r };
  },
  neighbors(q: number, r: number) {
    const offsets = (q & 1) ? ODD_COL_NEIGHBORS : EVEN_COL_NEIGHBORS;
    return offsets.map(d => ({ x: q + d.x, y: r + d.y }));
  },
  distance(x1: number, y1: number, x2: number, y2: number) {
    const a = offsetToCube(x1, y1);
    const b = offsetToCube(x2, y2);
    return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y), Math.abs(a.z - b.z));
  },
  isAdjacent(x1: number, y1: number, x2: number, y2: number) {
    const offsets = (x1 & 1) ? ODD_COL_NEIGHBORS : EVEN_COL_NEIGHBORS;
    const dx = x2 - x1;
    const dy = y2 - y1;
    return offsets.some(d => d.x === dx && d.y === dy);
  },
  drawTilePath(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
    // Flat-top hexagon path
    const radius = scaleTileExtent(size, HEX_PROJECTION);
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angleDeg = 60 * i;
      const angleRad = (Math.PI / 180) * angleDeg;
      const vx = cx + radius * Math.cos(angleRad);
      const vy = cy + radius * Math.sin(angleRad);
      if (i === 0) {
        ctx.moveTo(vx, vy);
      } else {
        ctx.lineTo(vx, vy);
      }
    }
    ctx.closePath();
  },
  mapPixelDimensions(width: number, height: number, size: number) {
    const projection = resolveProjection(size, HEX_PROJECTION);
    const w = (width - 1) * projection.stepX + projection.renderWidth;
    const h = height * projection.stepY + projection.stepY * 0.5;
    return { w, h };
  },
});
